import { memo } from 'react';
import { Group, Rect, Text } from 'react-konva';
import { Foup2D } from './Foup2D';
import { OHT_2D_DESIGN as D, getOht2DLayout } from './ohtGeometry';
import {
  OHT_2D_DEFAULT_HEIGHT,
  OHT_2D_DEFAULT_WIDTH,
  OHT_2D_SIMPLE_ZOOM,
  OHT_COLORS,
  resolveOhtStatusColor,
} from './oht2dPalette';
import type { Oht2DProps } from './types';

const LOGO_OFFSETS = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
] as const;

/**
 * Top-view FAB OHT matched to the product reference illustration: FOUP hood
 * behind a dark carriage band, two white gripper pods below.
 * Design space ≈ 148×118; scaled into the element footprint.
 * Rect only — no Circle body/panel geometry.
 */
export const Oht2D = memo(function Oht2D({
  x = 0,
  y = 0,
  width = OHT_2D_DEFAULT_WIDTH,
  height = OHT_2D_DEFAULT_HEIGHT,
  scale = 1,
  rotation = 0,
  centered = true,
  status = 'idle',
  showFoup = true,
  foupKind = 'raw',
  name,
  showLabel: _showLabel = true,
  selected = false,
  hovered = false,
  detail = 'auto',
  zoomLevel = 1,
  opacity = 1,
  listening = false,
  onClick,
  onDblClick,
  onMouseEnter,
  onMouseLeave,
}: Oht2DProps) {
  const simple =
    detail === 'simple' || (detail === 'auto' && zoomLevel < OHT_2D_SIMPLE_ZOOM);
  const statusColor = resolveOhtStatusColor(status);

  const { s, ox, oy } = getOht2DLayout(width, height);
  const u = (n: number) => n * s;
  const graphicTop = showFoup ? D.foup.y : D.band.y;
  const graphicBottom = D.podL.y + D.podL.h;

  const originX = centered ? x - width / 2 : x;
  const originY = centered ? y - height / 2 : y;
  // Match reference outline weight (thin charcoal), not a thick world-unit frame.
  const sw = Math.min(u(D.stroke), 1.25 / Math.max(zoomLevel, 0.05));

  const renderPod = (pod: { x: number; y: number; w: number; h: number; r: number }) => (
    <Rect
      x={u(pod.x)}
      y={u(pod.y)}
      width={u(pod.w)}
      height={u(pod.h)}
      cornerRadius={u(pod.r)}
      fillLinearGradientStartPoint={{ x: 0, y: 0 }}
      fillLinearGradientEndPoint={{ x: 0, y: u(pod.h) }}
      fillLinearGradientColorStops={[
        0,
        OHT_COLORS.body,
        0.65,
        OHT_COLORS.body,
        1,
        OHT_COLORS.bodyShade,
      ]}
      stroke={OHT_COLORS.outerFrame}
      strokeWidth={sw}
      perfectDrawEnabled={false}
      shadowForStrokeEnabled={false}
    />
  );

  return (
    <Group
      x={originX}
      y={originY}
      scaleX={scale}
      scaleY={scale}
      rotation={rotation}
      opacity={opacity}
      listening={listening}
      onClick={onClick}
      onDblClick={onDblClick}
      onTap={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Rect
        width={width}
        height={height}
        fill="transparent"
        listening={listening}
        perfectDrawEnabled={false}
      />

      <Group x={ox} y={oy} listening={false}>
        {showFoup ? (
          <Foup2D
            x={u(D.foup.x)}
            y={u(D.foup.y)}
            width={u(D.foup.w)}
            height={u(D.foup.h)}
            foupKind={foupKind}
            strokeWidth={sw}
            detailed={!simple}
          />
        ) : null}

        {renderPod(D.podL)}
        {renderPod(D.podR)}

        {/* Dark carriage band over the rail */}
        <Rect
          x={u(D.band.x)}
          y={u(D.band.y)}
          width={u(D.band.w)}
          height={u(D.band.h)}
          fill={OHT_COLORS.carriage}
          cornerRadius={u(D.band.r)}
          stroke={OHT_COLORS.outerFrame}
          strokeWidth={sw}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
        />

        {!simple && (
          <>
            <Rect
              x={u(D.bandSheen.x)}
              y={u(D.bandSheen.y)}
              width={u(D.bandSheen.w)}
              height={u(D.bandSheen.h)}
              fill={OHT_COLORS.carriageSheen}
              cornerRadius={u(D.bandSheen.r)}
              perfectDrawEnabled={false}
            />

            <Rect
              name="oht-status-light"
              x={u(D.lightL.x)}
              y={u(D.lightL.y)}
              width={u(D.lightL.w)}
              height={u(D.lightL.h)}
              fill={statusColor}
              cornerRadius={u(D.lightL.r)}
              perfectDrawEnabled={false}
            />
            <Rect
              name="oht-status-light"
              x={u(D.lightR.x)}
              y={u(D.lightR.y)}
              width={u(D.lightR.w)}
              height={u(D.lightR.h)}
              fill={statusColor}
              cornerRadius={u(D.lightR.r)}
              perfectDrawEnabled={false}
            />

            {name ? (
              <Text
                x={u(D.podL.x)}
                y={u(D.nameText.y)}
                width={u(D.podL.w)}
                text={name}
                align="center"
                fontSize={u(D.nameText.fontSize)}
                fontStyle="bold"
                fill={OHT_COLORS.podText}
                wrap="none"
                ellipsis
                perfectDrawEnabled={false}
              />
            ) : null}

            {LOGO_OFFSETS.map(([dx, dy]) => (
              <Rect
                key={`${dx}:${dy}`}
                x={u(D.logo.cx + dx * D.logo.spread - D.logo.dot / 2)}
                y={u(D.logo.cy + dy * D.logo.spread - D.logo.dot / 2)}
                width={u(D.logo.dot)}
                height={u(D.logo.dot)}
                cornerRadius={u(D.logo.dot / 2)}
                fill={OHT_COLORS.logo}
                perfectDrawEnabled={false}
              />
            ))}
            <Text
              x={u(D.podR.x)}
              y={u(D.logoText.y)}
              width={u(D.podR.w)}
              text="BLI"
              align="center"
              fontSize={u(D.logoText.fontSize)}
              fontStyle="bold"
              fill={OHT_COLORS.podText}
              perfectDrawEnabled={false}
            />
          </>
        )}

        {simple && (
          <Rect
            name="oht-status-light"
            x={u(D.band.x + D.band.w / 2 - 12)}
            y={u(D.lightL.y)}
            width={u(24)}
            height={u(D.lightL.h)}
            fill={statusColor}
            cornerRadius={u(D.lightL.r)}
            perfectDrawEnabled={false}
          />
        )}

        {(selected || hovered) && (
          <Rect
            x={u(D.band.x - D.selectionPad)}
            y={u(graphicTop - D.selectionPad)}
            width={u(D.band.w + D.selectionPad * 2)}
            height={u(graphicBottom - graphicTop + D.selectionPad * 2)}
            fillEnabled={false}
            stroke={selected ? OHT_COLORS.selection : OHT_COLORS.hover}
            strokeWidth={sw}
            cornerRadius={u(10)}
            perfectDrawEnabled={false}
            shadowForStrokeEnabled={false}
          />
        )}
      </Group>
    </Group>
  );
});
